class PrinterSearch extends HTMLElement {
    #topManufacturers = ["Brother", "Canon", "Epson", "HP / Hewlett Packard", "Konica Minolta", "Kyocera", "Lexmark", "OKI", "Ricoh", "Samsung", "Xerox"];
    #otherManufacturers = [
        "C.Itoh",
        "Fujitsu",
        "Olivetti",
        "Toshiba",
        "Dell",
        "Tally Genicom",
        "Panasonic",
        "Star Micronics",
        "Francotyp",
        "Philips",
        "IBM",
        "Olympia",
        "Kodak",
        "NEC",
        "KIP",
        "Xante",
        "Develop",
        "Sagem",
        "NRG",
        "Lanier",
        "Sharp",
        "Seikosha",
        "Dymo",
        "Wincor-Nixdorf",
        "Mimaki",
        "Triumph-Adler",
        "Casio",
        "Pitney Bowes",
        "Utax",
    ];

    constructor() {
        super();

        this.state = {
            selectedModel: null,
            selectedManufacturer: null,
        };

        this.init();
    }

    async init() {
        await this.loadManufacturers();
        this.initializeEvents();
    }

    initializeEvents() {
        this.manufacturerSearchInput.addEventListener("input", () => {
            this.filterManufacturers(this.manufacturerSearchInput.value);
        });

        this.modelSearchInput.addEventListener("input", () => {
            this.filterModels(this.modelSearchInput.value);
        });

        this.manufacturerHeader.addEventListener("click", () => {
            this.modelContainer.classList.remove("opened");
            this.manufacturerContainer.classList.toggle("opened");
            this.manufacturerSearchInput.focus();
        });

        this.modelHeader.addEventListener("click", () => {
            this.manufacturerContainer.classList.remove("opened");
            this.modelContainer.classList.toggle("opened");
            this.modelSearchInput.focus();
        });

        this.form.addEventListener("submit", (e) => {
            e.preventDefault();

            if (this.state.selectedModel?.path) {
                window.location.href = this.state.selectedModel.path;
            }
        });
    }

    /** @returns {Promise<void>} */
    async loadManufacturers() {
        const manufacturers = await PrintersApi.getManufacturers({ limit: 999999 });
        const mapping = {
            "Top-Hersteller": [],
            "Weitere Hersteller": [],
            "Sonstige Hersteller": [],
        };

        for (const manufacturer of manufacturers) {
            if (this.#topManufacturers.includes(manufacturer.name)) {
                mapping["Top-Hersteller"].push(manufacturer);
            } else if (this.#otherManufacturers.includes(manufacturer.name)) {
                mapping["Weitere Hersteller"].push(manufacturer);
            } else {
                mapping["Sonstige Hersteller"].push(manufacturer);
            }
        }

        const ul = this.manufacturerList;

        ul.innerHTML = "";

        for (const [label, items] of Object.entries(mapping)) {
            const labelLi = document.createElement('li');
            const labelStrong = document.createElement('strong');

            labelStrong.innerText = label;
            labelLi.appendChild(labelStrong);
            ul.appendChild(labelLi);

            for (const item of items) {
                const li = document.createElement("li");
                const a = document.createElement("a");

                a.href = "#";
                a.innerText = item.name;
                a.dataset.id = item.id;

                a.addEventListener("click", (e) => {
                    e.preventDefault();

                    this.state.selectedManufacturer = item;
                    this.state.selectedModel = null;
                    this.manufacturerHeaderLabel.innerText = item.name;
                    this.manufacturerContainer.classList.remove("opened");
                    this.modelContainer.classList.remove("disabled");
                    this.submitButton.setAttribute("disabled", "disabled");
                    this.loadModels(item.id);
                });

                li.appendChild(a);
                ul.appendChild(li);
            }
        }
    }

    /**
     * @param {string} manufacturerId
     * @returns {Promise<void>}
     */
    async loadModels(manufacturerId) {
        const models = await PrintersApi.getModels({ manufacturer_id: manufacturerId, limit: 999999 });
        const ul = this.modelList;

        ul.innerHTML = "";

        for (const model of models) {
            if (!model.path) continue;

            const li = document.createElement("li");
            const a = document.createElement("a");

            a.href = "#";
            a.innerText = model.full_name;
            a.dataset.id = model.id;

            a.addEventListener("click", (e) => {
                e.preventDefault();

                this.state.selectedModel = model;
                this.modelHeaderLabel.innerText = model.full_name;
                this.modelContainer.classList.remove("opened");
                this.submitButton.removeAttribute("disabled");
            });

            li.appendChild(a);
            ul.appendChild(li);
        }

        if (models.length) {
            this.modelContainer.classList.add("opened");
        }
    }

    /** @param {string} search */
    filterManufacturers(search) {
        const searchKeywords = search ? search.toLowerCase().split(/[-_\s]/g) : [];

        for (const li of this.manufacturerResults) {
            const show = search.length < 1 || searchKeywords.every((k) => li.innerText.toLowerCase().includes(k));

            li.style.display = show ? "block" : "none";
        }
    }

    /** @param {string} search */
    filterModels(search) {
        const searchKeywords = search ? search.toLowerCase().split(/[-_\s]/g) : [];

        for (const li of this.modelResults) {
            const show = search.length < 1 || searchKeywords.every((k) => li.innerText.toLowerCase().includes(k));

            li.style.display = show ? "block" : "none";
        }
    }

    /** Manufacturers */
    /** @returns {HTMLDivElement} */
    get manufacturerContainer() {
        return this.querySelector("div.searchable-select-manufacturer");
    }

    /** @returns {HTMLDivElement} */
    get manufacturerContent() {
        return this.manufacturerContainer.querySelector("div.searchable-select--content");
    }

    /** @returns {HTMLDivElement} */
    get manufacturerHeader() {
        return this.manufacturerContainer.querySelector("div.searchable-select--header");
    }

    /** @returns {HTMLDivElement} */
    get manufacturerHeaderLabel() {
        return this.manufacturerHeader.querySelector("span");
    }

    /** @returns {HTMLInputElement} */
    get manufacturerSearchInput() {
        return this.manufacturerContainer.querySelector("input");
    }

    /** @returns {HTMLUListElement} */
    get manufacturerList() {
        return this.manufacturerContainer.querySelector("ul");
    }

    /** @returns {HTMLLIElement[]} */
    get manufacturerResults() {
        return this.manufacturerList.querySelectorAll("li");
    }

    /** Models */
    /** @returns {HTMLDivElement} */
    get modelContainer() {
        return this.querySelector("div.searchable-select-model");
    }

    /** @returns {HTMLDivElement} */
    get modelContent() {
        return this.modelContainer.querySelector("div.searchable-select--content");
    }

    /** @returns {HTMLDivElement} */
    get modelHeader() {
        return this.modelContainer.querySelector("div.searchable-select--header");
    }

    /** @returns {HTMLDivElement} */
    get modelHeaderLabel() {
        return this.modelHeader.querySelector("span");
    }

    /** @returns {HTMLInputElement} */
    get modelSearchInput() {
        return this.modelContainer.querySelector("input");
    }

    /** @returns {HTMLUListElement} */
    get modelList() {
        return this.modelContainer.querySelector("ul");
    }

    /** @returns {HTMLLIElement[]} */
    get modelResults() {
        return this.modelList.querySelectorAll("li");
    }

    /** @returns {HTMLFormElement} */
    get form() {
        return this.querySelector("form");
    }

    /** @returns {HTMLButtonElement} */
    get submitButton() {
        return this.querySelector('form button[type="submit"]');
    }
}

customElements.define("printer-search", PrinterSearch);
